import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Upload, Sparkles, Loader2, Trash2 } from "lucide-react";
import StatusPill from "@/components/StatusPill";
import { TESTIDS } from "@/constants/testIds";

export default function KeywordsTab({ projectId, onRefresh }) {
  const [keywords, setKeywords] = useState([]);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("");
  const fileRef = useRef(null);

  async function load() {
    try { const { data } = await api.get("/keywords", { params: { project_id: projectId } }); setKeywords(data); }
    catch { toast.error("Failed to load keywords"); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  async function handleUpload(e) {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("project_id", projectId);
    fd.append("file", file);
    try {
      const { data } = await api.post("/keywords/import", fd);
      toast.success(`Imported ${data.inserted} keywords (${data.skipped} skipped)`);
      load(); onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "CSV import failed");
    } finally { setBusy(false); fileRef.current.value = ""; }
  }

  async function seedDemo() {
    setBusy(true);
    try {
      const { data } = await api.post(`/keywords/seed-demo`, null, { params: { project_id: projectId } });
      toast.success(`Loaded ${data.inserted} demo keywords (${data.skipped} skipped)`);
      load(); onRefresh?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Demo load failed"); }
    finally { setBusy(false); }
  }

  async function del(id) {
    await api.delete(`/keywords/${id}`); load(); onRefresh?.();
  }

  const filtered = keywords.filter((k) => !filter || k.keyword.includes(filter.toLowerCase()));

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <label className="inline-flex items-center gap-2 text-xs px-3 py-2 border border-border hover:border-primary rounded-sm cursor-pointer transition-colors">
          <Upload className="w-3 h-3" /> Upload CSV
          <input data-testid={TESTIDS.kwUploadInput} ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleUpload} />
        </label>
        <button
          data-testid={TESTIDS.kwSeedDemoBtn}
          onClick={seedDemo} disabled={busy}
          className="inline-flex items-center gap-2 text-xs px-3 py-2 border border-border hover:border-primary disabled:opacity-50 rounded-sm transition-colors"
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Seed Home Services demo (50 keywords)
        </button>
        <input
          value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter…"
          className="ml-auto bg-background border border-border focus:border-primary outline-none px-3 py-2 text-xs rounded-sm txt-mono w-48"
        />
      </div>
      <div className="text-xs text-muted-foreground mb-2">{filtered.length} of {keywords.length} keyword{keywords.length !== 1 ? "s" : ""}</div>
      <div className="border border-border overflow-x-auto">
        <table className="w-full text-xs txt-mono" data-testid={TESTIDS.kwTable}>
          <thead>
            <tr className="bg-secondary border-b border-border text-left">
              <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">Keyword</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">Volume</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">Intent</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">Service</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">Geo</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">Status</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, 500).map((k) => (
              <tr key={k.id} className="border-b border-border last:border-b-0 hover:bg-secondary/40">
                <td className="px-3 py-2 max-w-md truncate">{k.keyword}</td>
                <td className="px-3 py-2 stat-num">{k.volume ?? "—"}</td>
                <td className="px-3 py-2">{k.intent_hint || k.intent || "—"}</td>
                <td className="px-3 py-2">{k.service_hint || k.service || "—"}</td>
                <td className="px-3 py-2">{[k.city_hint || k.city, (k.state_hint || k.state || "").toUpperCase()].filter(Boolean).join(", ") || "—"}</td>
                <td className="px-3 py-2"><StatusPill status={k.status} /></td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => del(k.id)} className="text-muted-foreground hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">No keywords yet. Upload a CSV or seed the demo.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
